/** Maintained deterministic territory loop. Grid cells are authoritative; the
 * actor and continuously moving hazards use world-space centres. The outer
 * boundary begins claimed. A capture-held route through unclaimed cells closes
 * when it returns to claimed territory, then flood fill preserves every region
 * containing a hazard and claims the other enclosed regions. */
import { advanceKinematicBody2d, overlapShape2d, type KinematicBody2d } from './collision2d.js';
import { claimObjective, createObjectivePossession, setObjectivePossession } from './contestedObjective.js';

export type TerritoryVariantV1 = 'standard' | 'alternate';
export type TerritoryCommandV1 = { type: 'territory'; dx: -1 | 0 | 1; dy: -1 | 0 | 1; capture: boolean };
export interface TerritoryCellV1 { col: number; row: number }
export interface TerritoryHazardConfigV1 { id: string; x: number; y: number; vx: number; vy: number; radius: number }
export interface TerritoryConfigV1 {
  profile: 'territory@1';
  variant: TerritoryVariantV1;
  arena: { columns: number; rows: number; start: TerritoryCellV1; hazards: TerritoryHazardConfigV1[] };
  rules: { lives: number; moveCadenceTicks: number; invulnerabilityTicks: number; hitFlashTicks: number;
    timeLimitTicks: number; targetClaimedCells: number; cellScore: number; completionScore: number };
}
export interface TerritorySnapshotV1 {
  tick: number; variant: TerritoryVariantV1;
  arena: { columns: number; rows: number };
  actor: TerritoryCellV1 & { x: number; y: number; vx: number; vy: number; facingX: -1 | 0 | 1; facingY: -1 | 0 | 1;
    invulnerable: boolean; hitStunned: boolean };
  hazards: Array<TerritoryHazardConfigV1>;
  claimedCells: TerritoryCellV1[]; trailCells: TerritoryCellV1[];
  capture: { ownerSlot: number | null; progress: number };
  claimedCount: number; targetClaimedCells: number; totalCells: number;
  score: number; lives: number; hitsTaken: number; timeLeft: number;
  phase: 'active' | 'won' | 'lost'; lossCause: null | 'lives' | 'time';
}
export type TerritoryEventV1 =
  | { kind: 'trail-start'; tick: number; data: { slot: number; col: number; row: number } }
  | { kind: 'trail-step'; tick: number; data: { slot: number; col: number; row: number; length: number } }
  | { kind: 'capture'; tick: number; data: { slot: number; x: number; y: number; cells: number; claimedCells: number; score: number } }
  | { kind: 'hurt'; tick: number; data: { slot: number; hazardId: string | null; cause: 'actor' | 'trail' | 'break' | 'self-cross'; lives: number } }
  | { kind: 'won'; tick: number; data: { slot: number; score: number; claimedCells: number } }
  | { kind: 'lost'; tick: number; data: { slot: number; cause: 'lives' | 'time'; score: number } };

export function territoryConfigV1(variant: TerritoryVariantV1 = 'standard'): TerritoryConfigV1 {
  if (variant !== 'standard' && variant !== 'alternate') throw new RangeError('Unsupported territory variant');
  const common = { lives: 3, moveCadenceTicks: 2, invulnerabilityTicks: 12, hitFlashTicks: 5,
    timeLimitTicks: 900, cellScore: 10, completionScore: 250 };
  return variant === 'standard' ? {
    profile: 'territory@1', variant,
    arena: { columns: 12, rows: 10, start: { col: 3, row: 0 },
      hazards: [{ id: 'sweeper', x: 8.5, y: 5.5, vx: 0.9, vy: 0.6, radius: 0.34 }] },
    rules: { ...common, targetClaimedCells: 64 },
  } : {
    profile: 'territory@1', variant,
    arena: { columns: 12, rows: 10, start: { col: 0, row: 4 },
      hazards: [{ id: 'drifter', x: 6.5, y: 7.5, vx: 1.05, vy: -0.45, radius: 0.34 }] },
    rules: { ...common, targetClaimedCells: 80 },
  };
}

function record(value: unknown, keys: readonly string[]): asserts value is Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)
    || ![Object.prototype, null].includes(Object.getPrototypeOf(value))
    || Reflect.ownKeys(value).length !== keys.length
    || Object.keys(value).sort().join(',') !== [...keys].sort().join(',')
    || Object.values(Object.getOwnPropertyDescriptors(value)).some(d => !('value' in d))) {
    throw new RangeError('Invalid territory record');
  }
}
function finite(value: unknown, min: number, max: number): asserts value is number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < min || value > max) throw new RangeError('Invalid territory numeric bound');
}
function integer(value: unknown, min: number, max: number): asserts value is number {
  finite(value, min, max); if (!Number.isSafeInteger(value)) throw new RangeError('Territory count must be an integer');
}
function cellKey(cell: TerritoryCellV1): string { return `${cell.col},${cell.row}`; }
function orderedCells(keys: Iterable<string>): TerritoryCellV1[] {
  return [...keys].map(key => { const [col, row] = key.split(',').map(Number); return { col: col!, row: row! }; })
    .sort((a, b) => a.row - b.row || a.col - b.col);
}

/** Bounded strict validator. Admission may additionally pin the exact standard
 * or alternate data digest; this permits small isolated rule fixtures. */
export function validateTerritoryConfigV1(input: unknown): TerritoryConfigV1 {
  record(input, ['profile', 'variant', 'arena', 'rules']);
  if (input.profile !== 'territory@1' || !['standard', 'alternate'].includes(String(input.variant))) throw new RangeError('Unsupported territory profile');
  record(input.arena, ['columns', 'rows', 'start', 'hazards']);
  integer(input.arena.columns, 6, 40); integer(input.arena.rows, 6, 40);
  record(input.arena.start, ['col', 'row']);
  integer(input.arena.start.col, 0, input.arena.columns - 1); integer(input.arena.start.row, 0, input.arena.rows - 1);
  if (input.arena.start.col !== 0 && input.arena.start.col !== input.arena.columns - 1
    && input.arena.start.row !== 0 && input.arena.start.row !== input.arena.rows - 1) throw new RangeError('Territory start must be on the claimed boundary');
  if (!Array.isArray(input.arena.hazards) || input.arena.hazards.length < 1 || input.arena.hazards.length > 8) throw new RangeError('Invalid territory hazard count');
  const ids = new Set<string>();
  for (const hazard of input.arena.hazards) {
    record(hazard, ['id', 'x', 'y', 'vx', 'vy', 'radius']);
    if (typeof hazard.id !== 'string' || !/^[a-z][a-z0-9-]{0,39}$/.test(hazard.id) || ids.has(hazard.id)) throw new RangeError('Invalid territory hazard ID');
    ids.add(hazard.id); finite(hazard.radius, 0.1, 0.49);
    finite(hazard.x, 1 + hazard.radius, input.arena.columns - 1 - hazard.radius);
    finite(hazard.y, 1 + hazard.radius, input.arena.rows - 1 - hazard.radius);
    finite(hazard.vx, -12, 12); finite(hazard.vy, -12, 12);
    if (hazard.vx === 0 && hazard.vy === 0) throw new RangeError('Territory hazards must move continuously');
  }
  record(input.rules, ['lives','moveCadenceTicks','invulnerabilityTicks','hitFlashTicks','timeLimitTicks','targetClaimedCells','cellScore','completionScore']);
  integer(input.rules.lives, 1, 9); integer(input.rules.moveCadenceTicks, 1, 10);
  integer(input.rules.invulnerabilityTicks, 0, 300); integer(input.rules.hitFlashTicks, 0, input.rules.invulnerabilityTicks);
  integer(input.rules.timeLimitTicks, 1, 18000); integer(input.rules.cellScore, 1, 10000); integer(input.rules.completionScore, 0, 100000);
  const boundary = input.arena.columns * 2 + (input.arena.rows - 2) * 2;
  integer(input.rules.targetClaimedCells, boundary + 1, input.arena.columns * input.arena.rows);
  return structuredClone(input) as unknown as TerritoryConfigV1;
}

export function createTerritoryV1(input: TerritoryConfigV1) {
  const config = validateTerritoryConfigV1(input), { arena, rules } = config;
  const initialClaimed = new Set<string>();
  for (let row = 0; row < arena.rows; row++) for (let col = 0; col < arena.columns; col++) {
    if (col === 0 || row === 0 || col === arena.columns - 1 || row === arena.rows - 1) initialClaimed.add(cellKey({ col, row }));
  }
  const claimed = new Set(initialClaimed), trail = new Set<string>();
  let actor = { ...arena.start }, safe = { ...arena.start }, facingX: -1|0|1 = 1, facingY: -1|0|1 = 0;
  let motionX: -1|0|1 = 0, motionY: -1|0|1 = 0;
  let hazards: KinematicBody2d[] = arena.hazards.map(h => ({ id: h.id, position: { x: h.x, y: h.y }, velocity: { x: h.vx, y: h.vy } }));
  let tick = 0, score = 0, lives = rules.lives, hitsTaken = 0, grace = 0;
  let phase: TerritorySnapshotV1['phase'] = 'active', lossCause: TerritorySnapshotV1['lossCause'] = null;
  // Movement and capture describe held input, not a one-tick action. A host
  // may advance several simulation ticks between device samples; only a new
  // complete command (including an explicit release) changes the held state.
  let held: TerritoryCommandV1 = { type: 'territory', dx: 0, dy: 0, capture: false };
  let possession = createObjectivePossession();
  let events: TerritoryEventV1[] = [];
  const centre = (cell: TerritoryCellV1) => ({ x: cell.col + 0.5, y: cell.row + 0.5 });
  const lose = (cause: 'lives'|'time') => { phase = 'lost'; lossCause = cause; events.push({ kind: 'lost', tick, data: { slot: 0, cause, score } }); };
  const hurt = (cause: 'actor'|'trail'|'break'|'self-cross', hazardId: string|null) => {
    lives--; hitsTaken++; events.push({ kind: 'hurt', tick, data: { slot: 0, hazardId, cause, lives } });
    trail.clear(); possession = setObjectivePossession(possession, null, tick, 1); actor = { ...safe }; grace = rules.invulnerabilityTicks;
    if (lives <= 0) lose('lives');
  };
  const inBounds = (cell: TerritoryCellV1) => cell.col >= 0 && cell.row >= 0 && cell.col < arena.columns && cell.row < arena.rows;
  const resolveClosure = (): number => {
    const blocked = new Set([...claimed, ...trail]), seen = new Set<string>(), fill = new Set<string>();
    const hazardCells = new Set(hazards.map(h => `${Math.floor(h.position.x)},${Math.floor(h.position.y)}`));
    for (let row = 0; row < arena.rows; row++) for (let col = 0; col < arena.columns; col++) {
      const start = `${col},${row}`; if (blocked.has(start) || seen.has(start)) continue;
      const component: string[] = [], queue = [{ col, row }]; seen.add(start); let containsHazard = false;
      for (let index = 0; index < queue.length; index++) {
        const current = queue[index]!, key = cellKey(current); component.push(key); if (hazardCells.has(key)) containsHazard = true;
        for (const next of [{col:current.col-1,row:current.row},{col:current.col+1,row:current.row},{col:current.col,row:current.row-1},{col:current.col,row:current.row+1}]) {
          const nextKey = cellKey(next); if (inBounds(next) && !blocked.has(nextKey) && !seen.has(nextKey)) { seen.add(nextKey); queue.push(next); }
        }
      }
      if (!containsHazard) for (const key of component) fill.add(key);
    }
    const before = claimed.size; for (const key of trail) claimed.add(key); for (const key of fill) claimed.add(key);
    return claimed.size - before;
  };
  return {
    applyCommand(slot: number, command: unknown): boolean {
      if (slot !== 0 || phase !== 'active') return false;
      try { record(command, ['type','dx','dy','capture']); } catch { return false; }
      if (command.type !== 'territory' || ![-1,0,1].includes(command.dx as number) || ![-1,0,1].includes(command.dy as number)
        || typeof command.capture !== 'boolean' || (command.dx !== 0 && command.dy !== 0)) return false;
      held = { type: 'territory', dx: command.dx as -1|0|1, dy: command.dy as -1|0|1, capture: command.capture }; return true;
    },
    step(): void {
      tick++; const command = held;
      if (phase !== 'active') return;
      motionX = 0; motionY = 0;
      let pendingClosure = false;
      if (trail.size && !command.capture) hurt('break', null);
      if (phase === 'active' && tick % rules.moveCadenceTicks === 0 && (command.dx || command.dy)) {
        facingX = command.dx; facingY = command.dy;
        const next = { col: actor.col + command.dx, row: actor.row + command.dy };
        if (inBounds(next)) {
          const key = cellKey(next);
          if (trail.has(key)) hurt('self-cross', null);
          else if (claimed.has(key)) { actor = next; motionX = command.dx; motionY = command.dy; if (trail.size) pendingClosure = true; else safe = { ...next }; }
          else if (command.capture) {
            actor = next; motionX = command.dx; motionY = command.dy; trail.add(key);
            if (trail.size === 1) {
              possession = claimObjective(possession, [{ slot: 0, entityId: 'actor', distance: 0 }], { tick, pickupRadius: 0, cooldownTicks: 0, tieBreak: 'slot' });
              events.push({ kind: 'trail-start', tick, data: { slot: possession.ownerSlot!, ...next } });
            } else events.push({ kind: 'trail-step', tick, data: { slot: possession.ownerSlot!, ...next, length: trail.size } });
          }
        }
      }
      hazards = hazards.map((body, index) => advanceKinematicBody2d(body, 1/30, {
        minX: 1 + arena.hazards[index]!.radius, minY: 1 + arena.hazards[index]!.radius,
        maxX: arena.columns - 1 - arena.hazards[index]!.radius, maxY: arena.rows - 1 - arena.hazards[index]!.radius,
      }).body);
      if (grace > 0) grace--;
      else if (phase === 'active') {
        const actorPoint = centre(actor);
        const actorHit = hazards.find((body, index) => overlapShape2d({ kind:'circle', ...actorPoint, radius:0.34 },
          { kind:'circle', x:body.position.x, y:body.position.y, radius:arena.hazards[index]!.radius }));
        if (actorHit) hurt('actor', actorHit.id);
        else {
          const cut = hazards.find((body, index) => [...trail].some(key => {
            const [col,row] = key.split(',').map(Number); return overlapShape2d({kind:'aabb',x:col!+0.5,y:row!+0.5,w:0.74,h:0.74},
              {kind:'circle',x:body.position.x,y:body.position.y,radius:arena.hazards[index]!.radius});
          }));
          if (cut) hurt('trail', cut.id);
        }
      }
      if (phase !== 'active') return;
      if (pendingClosure && trail.size) {
        const ownerSlot = possession.ownerSlot;
        if (ownerSlot === null) throw new Error('Territory closure lost actor attribution');
        const cells = resolveClosure(); trail.clear(); safe = { ...actor }; possession = setObjectivePossession(possession, null, tick, 1);
        score += cells * rules.cellScore; events.push({ kind:'capture', tick, data:{ slot:ownerSlot, ...centre(actor), cells, claimedCells:claimed.size, score } });
        if (claimed.size >= rules.targetClaimedCells) {
          score += rules.completionScore; phase = 'won'; events.push({ kind:'won', tick, data:{ slot:ownerSlot, score, claimedCells:claimed.size } });
        }
      }
      if (phase === 'active' && tick >= rules.timeLimitTicks) lose('time');
    },
    snapshot(): TerritorySnapshotV1 {
      const point = centre(actor);
      return structuredClone({ tick, variant: config.variant, arena: { columns: arena.columns, rows: arena.rows },
        actor: { ...actor, ...point, vx: motionX / rules.moveCadenceTicks, vy: motionY / rules.moveCadenceTicks,
          facingX, facingY, invulnerable: grace > 0, hitStunned: grace > rules.invulnerabilityTicks - rules.hitFlashTicks },
        hazards: hazards.map((body,index) => ({ id:body.id,x:body.position.x,y:body.position.y,vx:body.velocity.x,vy:body.velocity.y,radius:arena.hazards[index]!.radius })),
        claimedCells: orderedCells(claimed), trailCells: orderedCells(trail), capture: { ownerSlot: possession.ownerSlot, progress: trail.size },
        claimedCount: claimed.size, targetClaimedCells: rules.targetClaimedCells, totalCells: arena.columns * arena.rows,
        score, lives, hitsTaken, timeLeft: Math.max(0, rules.timeLimitTicks - tick), phase, lossCause });
    },
    drainEvents(): TerritoryEventV1[] { const out = events; events = []; return out; },
  };
}
